import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardFooter,
  Container,
  Row,
  Col,
  Table,
  Button,
  Input,
  FormGroup,
  Badge,
  Spinner,
} from 'reactstrap';
import Header from 'components/Headers/Header.js';
import Paginacao from 'components/Paginacao';
import {
  FaSearch,
  FaSyncAlt,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaTimes,
  FaExclamationTriangle,
  FaFileExcel,
} from 'react-icons/fa';
import api from 'services/api';
import toast from 'react-hot-toast';
import exportToSheet from 'utils/exportToSheet';
import formatCurrency from 'utils/formatCurrency';
import {
  Th,
  Td,
  Tr,
  KeyBadge,
  SearchInputWrapper,
  DescricaoWrapper,
  ProdutoWrapper,
  ResponsiveLabel,
} from './styles';

export interface IMaterialSemValorItem {
  id: number;
  materialId: number;
  codigoSap: string | null;
  diteCodigo?: string | null;
  descricao: string | null;
  propostaId: number | null;
  propostaVersaoId: number | null;
  orcamentoId: number | null;
  produtoId: number | null;
  produtoNome: string | null;
  origem: string | null;
  organizacaoVenda: string | null;
  canalDistribuicao: string | null;
  centroLucro: string | null;
  setorAtividade: string | null;
  atividade: string | null;
  empresa?: string | null;
  valor: number | null;
  custo: number | null;
  custoImp: number | null;
  createdAt: string | null;
  statusSap: string | null;
  status: 'sem_valor_e_custo' | 'sem_valor' | 'sem_custo';
}

export interface IMaterialSemValorResposta {
  registros: IMaterialSemValorItem[];
  total: number;
  pagina: number;
  limite: number;
  totalPaginas: number;
  canaisDisponiveis?: string[];
}

const MateriaisSemValor: React.FC = () => {
  const [materiais, setMateriais] = useState<IMaterialSemValorItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(50);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [exportando, setExportando] = useState(false);

  const [buscaInput, setBuscaInput] = useState('');
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('todos');
  // Data inicial comentada temporariamente para busca sem limite de data
  // const [dataInicio, setDataInicio] = useState<string>(() => {
  //   const hoje = new Date();
  //   const dataTresMesesAtras = new Date(
  //     hoje.getFullYear(),
  //     hoje.getMonth() - 3,
  //     1,
  //   );
  //   const ano = dataTresMesesAtras.getFullYear();
  //   const mes = String(dataTresMesesAtras.getMonth() + 1).padStart(2, '0');
  //   return `${ano}-${mes}-01`;
  // });
  const [dataInicio] = useState<string>('');
  const [setor, setSetor] = useState('todos');
  const [setoresAtividade, setSetoresAtividade] = useState<string[]>([]);

  const [campoOrdenacao, setCampoOrdenacao] = useState<string>('createdAt');
  const [direcaoOrdenacao, setDirecaoOrdenacao] = useState<'asc' | 'desc'>(
    'desc',
  );

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get<IMaterialSemValorResposta>(
        '/material/sem-valor/listar',
        {
          params: {
            busca: busca || undefined,
            status: status !== 'todos' ? status : undefined,
            dataInicio: dataInicio || undefined,
            setorAtividade: setor !== 'todos' ? setor : undefined,
            pagina,
            limite,
            campoOrdenacao,
            direcaoOrdenacao,
          },
        },
      );

      const responseSetores = await api.get<string[]>('/produto/list-setores');

      const listaSetores = responseSetores.data as string[];

      setMateriais(response.data.registros || []);
      setTotal(response.data.total || 0);
      setTotalPaginas(response.data.totalPaginas || 1);
      if (listaSetores && listaSetores.length > 0) {
        setSetoresAtividade(listaSetores);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          'Erro ao carregar itens sem valor/custo',
      );
    } finally {
      setLoading(false);
    }
  }, [
    busca,
    status,
    dataInicio,
    setor,
    pagina,
    limite,
    campoOrdenacao,
    direcaoOrdenacao,
  ]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Debounce para busca textual
  useEffect(() => {
    const timer = setTimeout(() => {
      setBusca(buscaInput);
      setPagina(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [buscaInput]);

  const handleSort = (campo: string) => {
    if (campoOrdenacao === campo) {
      setDirecaoOrdenacao(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setCampoOrdenacao(campo);
      setDirecaoOrdenacao('asc');
    }
    setPagina(1);
  };

  const renderSortIcon = (campo: string) => {
    if (campoOrdenacao !== campo) {
      return <FaSort className="ml-1 text-muted" style={{ opacity: 0.35 }} />;
    }
    return direcaoOrdenacao === 'asc' ? (
      <FaSortUp className="ml-1 text-primary" />
    ) : (
      <FaSortDown className="ml-1 text-primary" />
    );
  };

  const handleExportarPlanilha = async () => {
    try {
      setExportando(true);
      toast.loading('Buscando dados para exportação...', {
        id: 'export-loading',
      });

      const response = await api.get<IMaterialSemValorResposta>(
        '/material/sem-valor/listar',
        {
          params: {
            busca: busca || undefined,
            status: status !== 'todos' ? status : undefined,
            dataInicio: dataInicio || undefined,
            setorAtividade: setor !== 'todos' ? setor : undefined,
            pagina: 1,
            limite: total > 0 ? total : 50000,
            campoOrdenacao,
            direcaoOrdenacao,
          },
        },
      );

      const lista = response.data?.registros || [];

      if (lista.length === 0) {
        toast.dismiss('export-loading');
        toast.error('Nenhum dado encontrado para exportar.');
        return;
      }

      // Função auxiliar para converter valores monetários em número puro
      const converteParaNumero = (
        val: number | string | null | undefined,
      ): number => {
        if (typeof val === 'number') {
          return val;
        }
        if (val !== null && val !== undefined && val !== '') {
          const num = Number(val);
          return Number.isNaN(num) ? 0 : num;
        }
        return 0;
      };

      // Mapeia estritamente as 12 colunas exibidas na tela
      const dadosPlanilha = lista.map(item => ({
        Proposta: item.propostaId ? String(item.propostaId) : '',
        Configuração: item.orcamentoId ? String(item.orcamentoId) : '',
        Produto: item.produtoNome || '',
        'Cód. SAP': item.codigoSap || '',
        'Descrição do Material': item.descricao || '',
        'Org. Venda': item.organizacaoVenda || '',
        'Canal Dist.': item.canalDistribuicao || '',
        'Centro Lucro': item.centroLucro || '',
        'Setor Ativ.': item.setorAtividade || '',
        'Status Sap': item.statusSap || '',
        'Valor Unitário': converteParaNumero(item.valor),
        'Custo Unitário': converteParaNumero(item.custo),
      }));

      exportToSheet(dadosPlanilha, 'Itens sem Valor ou Custo', true, [
        'Valor Unitário',
        'Custo Unitário',
      ]);

      toast.dismiss('export-loading');
      toast.success('Planilha exportada com sucesso!');
    } catch (error: any) {
      toast.dismiss('export-loading');
      toast.error(
        error?.response?.data?.message ||
          'Erro ao exportar dados para planilha',
      );
    } finally {
      setExportando(false);
    }
  };

  const renderTableBody = () => {
    if (loading) {
      return (
        <tr>
          <Td colSpan={12} className="text-center py-5">
            <Spinner color="primary" />
            <div className="mt-2 text-muted font-weight-bold">
              Carregando itens sem valor/custo...
            </div>
          </Td>
        </tr>
      );
    }

    if (materiais.length === 0) {
      return (
        <tr>
          <Td colSpan={12} className="text-center py-5">
            <FaExclamationTriangle size={32} className="text-warning mb-2" />
            <div className="text-muted font-weight-bold">
              Nenhum item sem valor ou custo encontrado com os filtros
              selecionados.
            </div>
          </Td>
        </tr>
      );
    }

    return materiais.map(item => (
      <Tr key={`${item.origem || 'item'}-${item.id}-${item.propostaId || ''}`}>
        <Td className="font-weight-600 text-dark text-nowrap">
          {item.propostaId || '-'}
        </Td>
        <Td className="text-nowrap">
          {item.orcamentoId ? (
            <span className="text-muted font-weight-500">
              {item.orcamentoId}
            </span>
          ) : (
            <span className="text-muted">-</span>
          )}
        </Td>
        <Td>
          <ProdutoWrapper className="font-weight-600 text-dark">
            {item.produtoNome || '-'}
          </ProdutoWrapper>
        </Td>
        <Td className="font-weight-bold text-dark text-nowrap">
          {item.codigoSap || '-'}
        </Td>
        <Td>
          <DescricaoWrapper>
            <span>{item.descricao || '-'}</span>
          </DescricaoWrapper>
        </Td>
        <Td className="text-center text-nowrap">
          {item.organizacaoVenda ? (
            <KeyBadge title="Organização de Venda">
              {item.organizacaoVenda}
            </KeyBadge>
          ) : (
            <span className="text-muted">-</span>
          )}
        </Td>
        <Td className="text-center text-nowrap">
          {item.canalDistribuicao ? (
            <KeyBadge title="Canal de Distribuição">
              {item.canalDistribuicao}
            </KeyBadge>
          ) : (
            <span className="text-muted">-</span>
          )}
        </Td>
        <Td className="text-center text-nowrap">
          {item.centroLucro ? (
            <KeyBadge title="Centro de Lucro">{item.centroLucro}</KeyBadge>
          ) : (
            <span className="text-muted">-</span>
          )}
        </Td>
        <Td className="text-center text-nowrap">
          {item.setorAtividade ? (
            <KeyBadge title="Setor de Atividade">
              {item.setorAtividade}
            </KeyBadge>
          ) : (
            <span className="text-muted">-</span>
          )}
        </Td>
        <Td className="text-center text-nowrap">
          {item.statusSap ? (
            <KeyBadge title="Status Sap">{item.statusSap}</KeyBadge>
          ) : (
            <span className="text-muted">-</span>
          )}
        </Td>
        <Td className="text-right text-nowrap">
          {item.valor !== null && item.valor > 0 ? (
            formatCurrency(item.valor)
          ) : (
            <span className="text-danger font-weight-bold">R$ 0,00</span>
          )}
        </Td>
        <Td className="text-right text-nowrap">
          {item.custo !== null && item.custo > 0 ? (
            formatCurrency(item.custo)
          ) : (
            <span className="text-danger font-weight-bold">R$ 0,00</span>
          )}
        </Td>
      </Tr>
    ));
  };

  return (
    <>
      <Header showCards={false} />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 bg-white">
                <Row className="align-items-center mb-3">
                  <Col xl="5" lg="4" md="12" className="mb-2 mb-lg-0">
                    <div className="d-flex align-items-center">
                      <div>
                        <h3
                          className="mb-0 text-dark font-weight-bold"
                          style={{ fontSize: '1.25rem' }}
                        >
                          Itens sem Valor / Custo
                        </h3>
                        <small className="text-muted">
                          Lista de materiais em propostas pendentes de
                          precificação
                        </small>
                      </div>
                    </div>
                  </Col>
                  <Col xl="7" lg="8" md="12" className="text-lg-right">
                    <div className="d-flex align-items-center justify-content-lg-end flex-wrap">
                      <Badge
                        color="light"
                        className="text-dark p-2 font-weight-bold mr-2 mb-1 mb-sm-0"
                        style={{ fontSize: '0.78rem' }}
                      >
                        Total: {total.toLocaleString('pt-BR')} ocorrências
                      </Badge>
                      <Button
                        color="primary"
                        size="sm"
                        outline
                        onClick={carregarDados}
                        disabled={loading || exportando}
                        title="Atualizar lista"
                        className="mr-2 mb-1 mb-sm-0"
                      >
                        <FaSyncAlt
                          className={loading ? 'fa-spin mr-1' : 'mr-1'}
                        />
                        {loading ? 'Atualizando...' : 'Atualizar'}
                      </Button>
                      <Button
                        color="success"
                        size="sm"
                        className="btn-icon mb-1 mb-sm-0"
                        style={{
                          backgroundColor: '#1a8c5b',
                          borderColor: '#1a8c5b',
                        }}
                        onClick={handleExportarPlanilha}
                        disabled={loading || exportando || total === 0}
                        title="Exportar todos os registros filtrados para planilha Excel"
                      >
                        <FaFileExcel
                          className={exportando ? 'fa-spin mr-1' : 'mr-1'}
                        />
                        {exportando ? 'Exportando...' : 'Exportar Planilha'}
                      </Button>
                    </div>
                  </Col>
                </Row>

                {/* Linha de Filtros e Busca */}
                <Row className="align-items-center">
                  <Col xl="5" lg="4" md="6" sm="12" className="mb-2 mb-xl-0">
                    <SearchInputWrapper>
                      <FaSearch className="search-icon" />
                      <Input
                        type="text"
                        placeholder="Buscar por Proposta, Configuração, Cód. SAP, Produto, Descrição..."
                        value={buscaInput}
                        onChange={e => setBuscaInput(e.target.value)}
                        className="form-control"
                      />
                      {buscaInput && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm position-absolute"
                          style={{
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 5,
                          }}
                          onClick={() => setBuscaInput('')}
                          title="Limpar busca"
                        >
                          <FaTimes className="text-muted" />
                        </button>
                      )}
                    </SearchInputWrapper>
                  </Col>

                  <Col xl="3" lg="3" md="3" sm="6" className="mb-2 mb-xl-0">
                    <FormGroup className="mb-0">
                      <Input
                        type="select"
                        value={status}
                        onChange={e => {
                          setStatus(e.target.value);
                          setPagina(1);
                        }}
                        className="form-control"
                      >
                        <option value="todos">Filtro: Todos</option>
                        <option value="sem_valor">Filtro: Sem Valor</option>
                        <option value="sem_custo">Filtro: Sem Custo</option>
                        <option value="sem_ambos">
                          Filtro: Ambos (Sem Valor e Sem Custo)
                        </option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col xl="2" lg="2" md="3" sm="6" className="mb-2 mb-xl-0">
                    <FormGroup className="mb-0">
                      <Input
                        type="select"
                        value={setor}
                        onChange={e => {
                          setSetor(e.target.value);
                          setPagina(1);
                        }}
                        className="form-control"
                      >
                        <option value="todos">Setor: Todos</option>
                        {setoresAtividade.map(c => (
                          <option key={c} value={c}>
                            Setor: {c}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>

                  {/* Campo de filtro de data comentado temporariamente */}
                  {/*
                  <Col xl="2" lg="2" md="3" sm="6" className="mb-2 mb-xl-0">
                    <FormGroup className="mb-0 d-flex align-items-center">
                      <span
                        className="text-muted mr-1"
                        style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                      >
                        Desde:
                      </span>
                      <Input
                        type="date"
                        value={dataInicio}
                        onChange={e => {
                          setDataInicio(e.target.value);
                          setPagina(1);
                        }}
                        className="form-control"
                        style={{ height: '40px' }}
                      />
                    </FormGroup>
                  </Col>
                  */}

                  <Col
                    xl="2"
                    lg="3"
                    md="12"
                    sm="6"
                    className="mb-2 mb-xl-0 text-md-right"
                  >
                    <div className="d-flex align-items-center justify-content-md-end">
                      <span
                        className="text-muted mr-2"
                        style={{ fontSize: '0.85rem' }}
                      >
                        Por pág:
                      </span>
                      <Input
                        type="select"
                        value={limite}
                        onChange={e => {
                          setLimite(Number(e.target.value));
                          setPagina(1);
                        }}
                        style={{ width: '85px' }}
                        className="form-control"
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                      </Input>
                    </div>
                  </Col>
                </Row>
              </CardHeader>

              {/* Tabela de Itens */}
              <Table
                className="align-items-center table-flush"
                responsive
                hover
              >
                <thead className="thead-light">
                  <tr>
                    <Th
                      sortable
                      onClick={() => handleSort('propostaId')}
                      title="Proposta"
                    >
                      Proposta {renderSortIcon('propostaId')}
                    </Th>
                    <Th
                      sortable
                      onClick={() => handleSort('orcamentoId')}
                      title="Configuração"
                    >
                      <ResponsiveLabel>
                        <span className="full-label">Configuração</span>
                        <span className="short-label">Config.</span>
                      </ResponsiveLabel>{' '}
                      {renderSortIcon('orcamentoId')}
                    </Th>
                    <Th
                      sortable
                      onClick={() => handleSort('produtoNome')}
                      title="Produto"
                    >
                      Produto {renderSortIcon('produtoNome')}
                    </Th>
                    <Th
                      sortable
                      onClick={() => handleSort('codigoSap')}
                      title="Código SAP"
                    >
                      Cód. SAP {renderSortIcon('codigoSap')}
                    </Th>
                    <Th
                      sortable
                      onClick={() => handleSort('descricao')}
                      title="Descrição do Material"
                    >
                      <ResponsiveLabel>
                        <span className="full-label">
                          Descrição do Material
                        </span>
                        <span className="short-label">Descrição</span>
                      </ResponsiveLabel>{' '}
                      {renderSortIcon('descricao')}
                    </Th>
                    <Th
                      sortable
                      onClick={() => handleSort('organizacaoVenda')}
                      title="Organização de Venda"
                    >
                      Org. Venda {renderSortIcon('organizacaoVenda')}
                    </Th>
                    <Th
                      sortable
                      onClick={() => handleSort('canalDistribuicao')}
                      title="Canal de Distribuição"
                    >
                      Canal Dist. {renderSortIcon('canalDistribuicao')}
                    </Th>
                    <Th
                      sortable
                      onClick={() => handleSort('centroLucro')}
                      title="Centro de Lucro"
                    >
                      Centro Lucro {renderSortIcon('centroLucro')}
                    </Th>
                    <Th
                      sortable
                      onClick={() => handleSort('setorAtividade')}
                      title="Setor de Atividade"
                    >
                      Setor Ativ. {renderSortIcon('setorAtividade')}
                    </Th>
                    <Th
                      sortable
                      onClick={() => handleSort('statusSap')}
                      title="Status Sap"
                    >
                      Status {renderSortIcon('statusSap')}
                    </Th>
                    <Th
                      className="text-right"
                      sortable
                      onClick={() => handleSort('valor')}
                      title="Valor Unitário"
                    >
                      Valor Unit. {renderSortIcon('valor')}
                    </Th>
                    <Th
                      className="text-right"
                      sortable
                      onClick={() => handleSort('custo')}
                      title="Custo Unitário"
                    >
                      Custo Unit. {renderSortIcon('custo')}
                    </Th>
                  </tr>
                </thead>
                <tbody>{renderTableBody()}</tbody>
              </Table>

              {/* Rodapé com Paginação Reutilizável */}
              <CardFooter className="py-4 bg-white">
                <Paginacao
                  pagina={pagina}
                  totalPaginas={totalPaginas}
                  total={total}
                  limite={limite}
                  loading={loading}
                  onPageChange={setPagina}
                />
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default MateriaisSemValor;
